"use client";

import Link from "next/link";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { DataQualityBadge } from "@/components/kpi-table/data-quality-badge";
import type { KpiDefinition } from "@/lib/kpi-config";
import { formatKpiValue } from "@/lib/kpi-config";
import type { Fundamentals } from "@/lib/data/types";
import type { DataQualityReport } from "@/lib/data/validation/types";

/**
 * Kompakte Kachel-Darstellung (Label + Hover-Info + aktueller Wert) fuer Kennzahlen ohne
 * Zeitreihe - die volle KpiComparisonTable waere hier unpassend, da deren "Median/Ø"- und
 * "Verlauf"-Spalten fuer diese Kennzahlen immer leer blieben. Kacheln ohne Wert fuer die
 * aktuelle Aktie werden konsequent weggelassen statt eines nackten "–" zu zeigen (z.B.
 * Schuldenquote bei Banken/Versicherern strukturell nicht verfuegbar). Gemeinsam von der
 * Finanzielle-Gesundheit-, Profitabilitaet- und Bewertung-Tab-Ansicht genutzt.
 */
export function KpiStatTiles({
  defs,
  fundamentals,
  currency,
  dataQuality,
}: {
  defs: KpiDefinition[];
  fundamentals: Fundamentals;
  currency?: string;
  dataQuality?: DataQualityReport;
}) {
  const withValue = defs.filter((def) => fundamentals[def.key] !== undefined);
  if (withValue.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {withValue.map((def) => (
        <div key={def.key}>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {def.label}
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Zugleich Link zum vollen Glossar-Eintrag (backlog.md -> "Glossar-
                    Verlinkung direkt aus jedem KPI-Tooltip") - siehe Begruendung in
                    glossary-term.tsx, warum kein Link INNERHALB von TooltipContent steht. */}
                <Link
                  href={`/glossar#${def.key}`}
                  aria-label={`Erklärung: ${def.label}`}
                  className="text-muted-foreground hover:text-ink"
                >
                  <Info className="h-3 w-3" />
                </Link>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">{def.tooltip}</TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            <div className="font-tabular text-2xl text-ink">
              {formatKpiValue(def.formatKind, fundamentals[def.key], currency)}
            </div>
            <DataQualityBadge validation={dataQuality?.metrics.find((m) => m.metric === def.key)} />
          </div>
        </div>
      ))}
    </div>
  );
}
